import { Disclosure } from '@headlessui/react';
import { StarIcon } from '@heroicons/react/20/solid';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import ImageGallery from '@/components/image-gallery';
import classNames from '@/lib/class-names';
import priceFormatted from '@/lib/price-formatted';
import type { ComponentProps, ElementType, ReactNode } from 'react';

// *****************************************
// *
// * ProductDetailRoot
// *
// *****************************************

const ProductDetailRootDefaultElement: ElementType = 'div';

interface ProductDetailRoot {
  <E extends ElementType = typeof ProductDetailRootDefaultElement>(props: ProductDetailRootProps<E>): JSX.Element;
  displayName: string;
}

type ProductDetailRootProps<E extends ElementType> = ProductDetailRootOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailRootOwnProps>;

type ProductDetailRootOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
  className?: string;
};

const ProductDetailRoot: ProductDetailRoot = ({
  as: Component = ProductDetailRootDefaultElement,
  children,
  className,
  ...rest
}) => {
  return (
    <Component className={className} {...rest}>
      {children}
    </Component>
  );
};

ProductDetailRoot.displayName = 'ProductDetail';

// *****************************************
// *
// * ProductDetailGallery
// *
// *****************************************

const ProductDetailGalleryDefaultElement: ElementType = 'div';

interface ProductDetailGallery {
  <E extends ElementType = typeof ProductDetailGalleryDefaultElement>(props: ProductDetailGalleryProps<E>): JSX.Element;
  displayName: string;
}

type ProductDetailGalleryProps<E extends ElementType> = ProductDetailGalleryOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailGalleryOwnProps>;

type ProductDetailGalleryOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  productMedia: {
    alt: string | null;
    height: number | null;
    src: string;
    width: number | null;
  }[];
  productName: string;
};

const ProductDetailGallery: ProductDetailGallery = ({
  as: Component = ProductDetailGalleryDefaultElement,
  productMedia,
  productName,
  ...rest
}) => {
  return (
    <Component {...rest}>
      <ImageGallery
        images={productMedia.map((media) => ({
          alt: media.alt ?? productName,
          height: media.height,
          name: productName,
          src: media.src,
          width: media.width,
        }))}
      />
    </Component>
  );
};

ProductDetailGallery.displayName = 'ProductDetail_Gallery';

// *****************************************
// *
// * ProductDetailName
// *
// *****************************************

const ProductDetailNameDefaultElement: ElementType = 'h1';

interface ProductDetailName {
  <E extends ElementType = typeof ProductDetailNameDefaultElement>(props: ProductDetailNameProps<E>): JSX.Element;
  displayName: string;
}

type ProductDetailNameProps<E extends ElementType> = ProductDetailNameOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailNameOwnProps>;

type ProductDetailNameOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
};

const ProductDetailName: ProductDetailName = ({
  as: Component = ProductDetailNameDefaultElement,
  children,
  ...rest
}) => {
  return <Component {...rest}>{children}</Component>;
};

ProductDetailName.displayName = 'ProductDetail_Name';

// *****************************************
// *
// * ProductDetailPrice
// *
// *****************************************

const ProductDetailPriceDefaultElement: ElementType = 'div';

interface ProductDetailPrice {
  <E extends ElementType = typeof ProductDetailPriceDefaultElement>(props: ProductDetailPriceProps<E>): JSX.Element;
  displayName: string;
}

type ProductDetailPriceProps<E extends ElementType> = ProductDetailPriceOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailPriceOwnProps>;

type ProductDetailPriceOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  className?: string;
  currency: string;
  innerWrapperClassName?: string;
  innerWrapperElement?: ElementType;
  priceList: number;
  priceListWithTax: number;
};

const ProductDetailPrice: ProductDetailPrice = ({
  as: Component = ProductDetailPriceDefaultElement,
  className,
  currency,
  innerWrapperClassName,
  innerWrapperElement: InnerWrapper = 'p',
  priceList,
  priceListWithTax,
  ...rest
}) => {
  const intl = useIntl();

  const formattedPrice = useMemo(() => {
    const price = priceFormatted(intl, currency, priceList);
    const formattedMsg = intl.formatMessage(
      {
        defaultMessage: '{price} bez DPH',
        description: 'Price without VAT labelled.',
        id: 'zyZ1/S',
      },
      { price },
    );

    // hook: fix an issue with no translated variables in strings...
    if (formattedMsg.includes('{price}')) {
      return formattedMsg.replace('{price}', price);
    }

    return formattedMsg;
  }, [currency, intl, priceList]);

  return (
    <Component className={className} {...rest}>
      <h2 className='sr-only'>Cena produktu</h2>
      <InnerWrapper className={innerWrapperClassName}>
        <span aria-describedby='price-detail-price-amount'>
          {
            formattedPrice
            // 'Cena na poptání'
          }
        </span>
        <span className='sr-only' id='price-detail-price-amount'>
          {'Ceníková cena produktu bez DPH'}
        </span>
      </InnerWrapper>
    </Component>
  );
};

ProductDetailPrice.displayName = 'ProductDetail_Price';

// *****************************************
// *
// * ProductDetailReviews
// *
// *****************************************

const ProductDetailReviewsDefaultElement: ElementType = 'div';

interface ProductDetailReviews {
  <E extends ElementType = typeof ProductDetailReviewsDefaultElement>(props: ProductDetailReviewsProps<E>): JSX.Element;
  displayName: string;
}

type ProductDetailReviewsProps<E extends ElementType> = ProductDetailReviewsOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailReviewsOwnProps>;

type ProductDetailReviewsOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  rating: number;
};

const ProductDetailReviews: ProductDetailReviews = ({
  as: Component = ProductDetailReviewsDefaultElement,
  rating,
  ...rest
}) => {
  return (
    <Component {...rest} className='mt-3'>
      <h3 className='sr-only'>Reviews</h3>
      <div className='flex items-center'>
        <div className='flex items-center'>
          {[0, 1, 2, 3, 4].map((rt) => (
            <StarIcon
              key={rt}
              aria-hidden='true'
              className={classNames(
                rating > rt ? 'text-primary-focus' : 'text-surface-suspend',
                'h-5 w-5 flex-shrink-0',
              )}
            />
          ))}
        </div>
        <p className='sr-only'>{rating} out of 5 stars</p>
      </div>
    </Component>
  );
};

ProductDetailReviews.displayName = 'ProductDetail_Reviews';

// *****************************************
// *
// * ProductDetailDescription
// *
// *****************************************

const ProductDetailDescriptionDefaultElement: ElementType = 'div';

interface ProductDetailDescription {
  <E extends ElementType = typeof ProductDetailDescriptionDefaultElement>(
    props: ProductDetailDescriptionProps<E>,
  ): JSX.Element;
  displayName: string;
}

type ProductDetailDescriptionProps<E extends ElementType> = ProductDetailDescriptionOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailDescriptionOwnProps>;

type ProductDetailDescriptionOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
};

const ProductDetailDescription: ProductDetailDescription = ({
  as: Component = ProductDetailDescriptionDefaultElement,
  children,
  ...rest
}) => {
  return (
    <Component {...rest}>
      <h3 className='sr-only'>Popis produktu</h3>
      {children}
    </Component>
  );
};

ProductDetailDescription.displayName = 'ProductDetail_Description';

// *****************************************
// *
// * ProductDetailVariants
// *
// *****************************************

const ProductDetailVariantsDefaultElement: ElementType = 'div';

interface ProductDetailVariants {
  <E extends ElementType = typeof ProductDetailVariantsDefaultElement>(
    props: ProductDetailVariantsProps<E>,
  ): JSX.Element;
  displayName: string;
}

type ProductDetailVariantsProps<E extends ElementType> = ProductDetailVariantsOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailVariantsOwnProps>;

type ProductDetailVariantsOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
};

const ProductDetailVariantsRoot: ProductDetailVariants = ({
  as: Component = ProductDetailVariantsDefaultElement,
  children,
  ...rest
}) => {
  return <Component {...rest}>{children}</Component>;
};

ProductDetailVariantsRoot.displayName = 'ProductDetail_Variants';

// *****************************************
// *
// * ProductDetailVariantsHeading
// *
// *****************************************

const ProductDetailVariantsHeadingDefaultElement: ElementType = 'h3';

interface ProductDetailVariantsHeading {
  <E extends ElementType = typeof ProductDetailVariantsHeadingDefaultElement>(
    props: ProductDetailVariantsHeadingProps<E>,
  ): JSX.Element;
  displayName: string;
}

type ProductDetailVariantsHeadingProps<E extends ElementType> = ProductDetailVariantsHeadingOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailVariantsHeadingOwnProps>;

type ProductDetailVariantsHeadingOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
  className?: string;
};

const ProductDetailVariantsHeading: ProductDetailVariantsHeading = ({
  as: Component = ProductDetailVariantsHeadingDefaultElement,
  children,
  ...rest
}) => {
  return <Component {...rest}>{children}</Component>;
};

ProductDetailVariantsHeading.displayName = 'ProductDetail_Variants_Heading';

// *****************************************
// *
// * ProductDetailVariants
// *
// *****************************************

const ProductDetailVariants = Object.assign(ProductDetailVariantsRoot, { Heading: ProductDetailVariantsHeading });

// *****************************************
// *
// * ProductDetailSpagettiRoot
// *
// *****************************************

const ProductDetailSpagettiDefaultElement: ElementType = 'section';

interface ProductDetailSpagetti {
  <E extends ElementType = typeof ProductDetailSpagettiDefaultElement>(
    props: ProductDetailSpagettiProps<E>,
  ): JSX.Element;
  displayName: string;
}

type ProductDetailSpagettiProps<E extends ElementType> = ProductDetailSpagettiOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailSpagettiOwnProps>;

type ProductDetailSpagettiOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
  headingElement?: ElementType;
  headingLabel?: string;
};

const ProductDetailSpagettiRoot: ProductDetailSpagetti = ({
  as: Component = ProductDetailSpagettiDefaultElement,
  children,
  headingElement: Heading = 'h2',
  headingLabel = 'Additional details',
  ...rest
}) => {
  return (
    <Component {...rest} aria-labelledby='details-heading'>
      <Heading aria-labelledby='details-heading' className='sr-only'>
        {headingLabel}
      </Heading>
      <div className='divide-surface-divid divide-y border-t'>{children}</div>
    </Component>
  );
};

ProductDetailSpagettiRoot.displayName = 'ProductDetail_Spagetti';

// *****************************************
// *
// * ProductDetailSpagettiPanel
// *
// *****************************************

const ProductDetailSpagettiPanelDefaultElement: ElementType = 'div';

interface ProductDetailSpagettiPanel {
  <E extends ElementType = typeof ProductDetailSpagettiPanelDefaultElement>(
    props: ProductDetailSpagettiPanelProps<E>,
  ): JSX.Element;
  displayName: string;
}

type ProductDetailSpagettiPanelProps<E extends ElementType> = ProductDetailSpagettiPanelOwnProps<E> &
  Omit<ComponentProps<E>, keyof ProductDetailSpagettiPanelOwnProps>;

type ProductDetailSpagettiPanelOwnProps<E extends ElementType = ElementType> = {
  as?: E;
  children: ReactNode;
  defaultOpen?: boolean;
  heading?: ReactNode;
  headingElement?: ElementType;
  panelProps?: {
    className?: string;
  };
};

const ProductDetailSpagettiPanel: ProductDetailSpagettiPanel = ({
  as = ProductDetailSpagettiPanelDefaultElement,
  children,
  defaultOpen,
  heading: headingContent = 'Additional details',
  headingElement: Heading = 'h3',
  panelProps,
  ...rest
}) => {
  return (
    <Disclosure as={'div'} defaultOpen={defaultOpen} {...rest}>
      {({ open }) => (
        <>
          <h3>
            <Disclosure.Button className='group relative flex w-full items-center justify-between py-6 text-left'>
              <span className={classNames(open ? 'text-primary-focus' : 'text-primary-suspend', 'text-sm font-medium')}>
                {headingContent}
              </span>
              <span className='ml-6 flex items-center'>
                {open ? (
                  <MinusIcon aria-hidden='true' className='block h-6 w-6 text-indigo-400 group-hover:text-indigo-500' />
                ) : (
                  <PlusIcon aria-hidden='true' className='block h-6 w-6 text-gray-400 group-hover:text-gray-500' />
                )}
              </span>
            </Disclosure.Button>
          </h3>
          <Disclosure.Panel
            as='div'
            {...panelProps}
            className={classNames('prose prose-sm pb-6', panelProps?.className)}
          >
            {children}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

ProductDetailSpagettiPanel.displayName = 'ProductDetail_Spagetti_Panel';

// *****************************************
// *
// * ProductDetailSpagetti
// *
// *****************************************

const ProductDetailSpagetti = Object.assign(ProductDetailSpagettiRoot, {
  Panel: ProductDetailSpagettiPanel,
});

// *****************************************
// *
// * ProductDetail
// *
// *****************************************

const ProductDetail = Object.assign(ProductDetailRoot, {
  Description: ProductDetailDescription,
  Gallery: ProductDetailGallery,
  Name: ProductDetailName,
  Price: ProductDetailPrice,
  Reviews: ProductDetailReviews,
  Spagetti: ProductDetailSpagetti,
  Variants: ProductDetailVariants,
});

export default ProductDetail;
